import { createFileRoute } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { ChevronLeft, ChevronRight, Loader2, ScrollText, Search, X } from 'lucide-react'
import { type FormEvent, useEffect, useState } from 'react'
import {
  type AdminAuditEvent,
  type AdminAuditPage,
  type AuditFilters,
  getAdminAuditEvents,
  PlatformApiError,
} from '@/lib/api'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/platform/audit')({
  component: PlatformAuditPage,
})

/**
 * Page size — small enough to render quickly, big enough that
 * paginating to "the last big batch of events" doesn't take ten
 * clicks. Backend caps at 200; we stay well under so individual
 * page loads are snappy.
 */
const PAGE_LIMIT = 50

/**
 * Action display metadata. Keys are the AuditAction values from
 * the backend's union; everything else falls through to a generic
 * grey badge with the raw action string.
 *
 * Tone classes come from the same palette the rest of the admin UI
 * uses, so the page reads consistently with /platform/tenants and
 * /platform/users.
 */
const ACTION_STYLES: Record<string, { label: string; tone: string }> = {
  // Tenant lifecycle
  'tenant.create': {
    label: 'Tenant created',
    tone: 'bg-success/10 text-success ring-success/25',
  },
  'tenant.rename': {
    label: 'Tenant renamed',
    tone: 'bg-chart-3/10 text-chart-3 ring-chart-3/25',
  },
  'tenant.pause': {
    label: 'Tenant paused',
    tone: 'bg-warning/10 text-warning ring-warning/25',
  },
  'tenant.resume': {
    label: 'Tenant resumed',
    tone: 'bg-success/10 text-success ring-success/25',
  },
  'tenant.schedule_deletion': {
    label: 'Deletion scheduled',
    tone: 'bg-destructive/10 text-destructive ring-destructive/25',
  },
  'tenant.cancel_deletion': {
    label: 'Deletion cancelled',
    tone: 'bg-success/10 text-success ring-success/25',
  },
  'tenant.hard_delete': {
    label: 'Tenant hard-deleted',
    tone: 'bg-destructive/10 text-destructive ring-destructive/25',
  },
  'tenant.impersonate': {
    label: 'Impersonate',
    tone: 'bg-chart-4/10 text-chart-4 ring-chart-4/25',
  },
  // Members
  'tenant_member.add': {
    label: 'Member added',
    tone: 'bg-success/10 text-success ring-success/25',
  },
  'tenant_member.remove': {
    label: 'Member removed',
    tone: 'bg-destructive/10 text-destructive ring-destructive/25',
  },
  // Licensing
  'license.grant': {
    label: 'License granted',
    tone: 'bg-success/10 text-success ring-success/25',
  },
  'license.revoke': {
    label: 'License revoked',
    tone: 'bg-destructive/10 text-destructive ring-destructive/25',
  },
  // Module catalog
  'module.update': {
    label: 'Module updated',
    tone: 'bg-chart-3/10 text-chart-3 ring-chart-3/25',
  },
  // Platform users
  'user.role_change': {
    label: 'Role changed',
    tone: 'bg-primary/10 text-primary ring-primary/25',
  },
}

/**
 * Set of all known actions for the filter dropdown. Sorted
 * alphabetically so the picker is predictable.
 */
const KNOWN_ACTIONS = Object.keys(ACTION_STYLES).sort()

function PlatformAuditPage() {
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'loaded'; page: AdminAuditPage }
    | { status: 'error'; message: string }
  >({ status: 'loading' })

  // Filter state — held separately from `appliedFilters` so the
  // user can edit the inputs without refetching on every keystroke.
  // The form's onSubmit promotes `draftFilters` into `appliedFilters`,
  // which is what the effect actually reads.
  const [draftFilters, setDraftFilters] = useState<AuditFilters>({})
  const [appliedFilters, setAppliedFilters] = useState<AuditFilters>({})
  const [offset, setOffset] = useState(0)

  useEffect(() => {
    const ctrl = new AbortController()
    setState({ status: 'loading' })
    getAdminAuditEvents({ ...appliedFilters, limit: PAGE_LIMIT, offset }, ctrl.signal)
      .then((page) => setState({ status: 'loaded', page }))
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        const message =
          err instanceof PlatformApiError && err.message ? err.message : 'Failed to load audit log'
        setState({ status: 'error', message })
      })
    return () => ctrl.abort()
  }, [appliedFilters, offset])

  function handleFilterSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setOffset(0)
    setAppliedFilters({ ...draftFilters })
  }

  function handleClearFilters() {
    setDraftFilters({})
    setAppliedFilters({})
    setOffset(0)
  }

  const hasFilters =
    !!appliedFilters.action ||
    !!appliedFilters.actorEmail ||
    !!appliedFilters.targetKind ||
    !!appliedFilters.targetId

  return (
    <div>
      <header className="mb-6">
        <h2 className="text-xl font-semibold tracking-tight sm:text-2xl">Audit log</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Every security-sensitive action on the platform — tenant lifecycle, license changes,
          module flag flips, role assignments, impersonations. Insert-only at the database level;
          rows here are immutable.
        </p>
      </header>

      <FilterBar
        draft={draftFilters}
        onDraftChange={setDraftFilters}
        onSubmit={handleFilterSubmit}
        onClear={handleClearFilters}
        hasFilters={hasFilters}
      />

      {state.status === 'loading' ? <LoadingState /> : null}
      {state.status === 'error' ? <ErrorState message={state.message} /> : null}
      {state.status === 'loaded' && state.page.events.length === 0 ? (
        <EmptyState hasFilters={hasFilters} />
      ) : null}
      {state.status === 'loaded' && state.page.events.length > 0 ? (
        <>
          <EventsTable events={state.page.events} />
          <Pagination
            offset={offset}
            limit={PAGE_LIMIT}
            total={state.page.total}
            shown={state.page.events.length}
            onPrev={() => setOffset((o) => Math.max(0, o - PAGE_LIMIT))}
            onNext={() => setOffset((o) => o + PAGE_LIMIT)}
          />
        </>
      ) : null}
    </div>
  )
}

function FilterBar({
  draft,
  onDraftChange,
  onSubmit,
  onClear,
  hasFilters,
}: {
  draft: AuditFilters
  onDraftChange: (filters: AuditFilters) => void
  onSubmit: (e: FormEvent<HTMLFormElement>) => void
  onClear: () => void
  hasFilters: boolean
}) {
  return (
    <form
      onSubmit={onSubmit}
      className="mb-4 flex flex-wrap items-end gap-2 rounded-2xl border border-border bg-card/60 p-3"
    >
      <Field label="Action">
        <select
          value={draft.action ?? ''}
          onChange={(e) => onDraftChange({ ...draft, action: e.target.value || undefined })}
          className={cn(
            'h-9 rounded-md border border-border bg-background px-2 text-xs',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background',
          )}
        >
          <option value="">All actions</option>
          {KNOWN_ACTIONS.map((action) => (
            <option key={action} value={action}>
              {ACTION_STYLES[action]?.label ?? action}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Actor email">
        <input
          type="email"
          placeholder="admin@example.com"
          value={draft.actorEmail ?? ''}
          onChange={(e) => onDraftChange({ ...draft, actorEmail: e.target.value || undefined })}
          className={cn(
            'h-9 w-52 rounded-md border border-border bg-background px-2 text-xs',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background',
          )}
        />
      </Field>
      <Field label="Target kind">
        <select
          value={draft.targetKind ?? ''}
          onChange={(e) => onDraftChange({ ...draft, targetKind: e.target.value || undefined })}
          className={cn(
            'h-9 rounded-md border border-border bg-background px-2 text-xs',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background',
          )}
        >
          <option value="">Any kind</option>
          <option value="tenant">tenant</option>
          <option value="user">user</option>
          <option value="license">license</option>
          <option value="module">module</option>
        </select>
      </Field>
      <Field label="Target id">
        <input
          type="text"
          placeholder="tenant uuid / user id / module key"
          value={draft.targetId ?? ''}
          onChange={(e) => onDraftChange({ ...draft, targetId: e.target.value || undefined })}
          className={cn(
            'h-9 w-64 rounded-md border border-border bg-background px-2 text-xs',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background',
          )}
        />
      </Field>
      <button
        type="submit"
        className={cn(
          'inline-flex h-9 items-center gap-1 rounded-md px-3 text-xs font-semibold',
          'bg-primary text-primary-foreground shadow-sm hover:opacity-90 transition-opacity',
        )}
      >
        <Search className="h-3.5 w-3.5" />
        Filter
      </button>
      {hasFilters ? (
        <button
          type="button"
          onClick={onClear}
          className={cn(
            'inline-flex h-9 items-center gap-1 rounded-md border border-border px-3 text-xs font-medium',
            'hover:bg-accent hover:text-accent-foreground transition-colors',
          )}
        >
          <X className="h-3.5 w-3.5" />
          Clear
        </button>
      ) : null}
    </form>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  // Plain div wrapper rather than <label> because the children are
  // opaque from this component's perspective — Biome can't statically
  // verify a control is inside a label, and adding htmlFor here
  // would require generating + threading an id. The input/select
  // children carry their own placeholders + accessible names.
  return (
    <div className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
      <span>{label}</span>
      {children}
    </div>
  )
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center gap-2 rounded-2xl border border-border bg-card/60 py-12 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      Loading audit events…
    </div>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
      {message}
    </div>
  )
}

function EmptyState({ hasFilters }: { hasFilters: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-2xl border border-dashed border-border bg-card/60 p-10 text-center"
    >
      <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/15">
        <ScrollText className="h-5 w-5" />
      </div>
      <h3 className="mt-4 text-lg font-semibold">
        {hasFilters ? 'No matching audit events' : 'No audit events yet'}
      </h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
        {hasFilters
          ? 'Try widening your filters — the audit log only stores actions taken since the audit log was first deployed, so older events are not retroactive.'
          : 'Once admins start taking actions on the platform, they will appear here. The cron also writes its own audit lines for hard-deletes.'}
      </p>
    </motion.div>
  )
}

function EventsTable({ events }: { events: AdminAuditEvent[] }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
    >
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">When</th>
              <th className="px-4 py-2.5 text-left font-semibold">Action</th>
              <th className="px-4 py-2.5 text-left font-semibold">Actor</th>
              <th className="px-4 py-2.5 text-left font-semibold">Target</th>
              <th className="px-4 py-2.5 text-left font-semibold">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {events.map((event) => (
              <EventRow key={event.id} event={event} />
            ))}
          </tbody>
        </table>
      </div>
    </motion.div>
  )
}

function EventRow({ event }: { event: AdminAuditEvent }) {
  const failed = event.status === 'failure'
  return (
    <tr className={cn('hover:bg-accent/40', failed && 'bg-destructive/5')}>
      <td className="px-4 py-3 align-top text-xs text-muted-foreground whitespace-nowrap">
        <div title={event.occurredAt}>{formatRelativeTime(event.occurredAt)}</div>
        <div className="text-[10px] opacity-70">{formatExactTime(event.occurredAt)}</div>
      </td>
      <td className="px-4 py-3 align-top">
        <ActionBadge action={event.action} />
        {failed ? (
          <span className="ml-1 inline-flex items-center rounded-full bg-destructive/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-destructive ring-1 ring-destructive/25">
            Failed
          </span>
        ) : null}
      </td>
      <td className="px-4 py-3 align-top text-xs">
        {event.actorKind === 'system' ? (
          <span className="inline-flex items-center rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground ring-1 ring-border">
            {event.actorEmail ?? 'system'}
          </span>
        ) : (
          <div>
            <div className="font-medium">{event.actorEmail ?? '—'}</div>
            {event.actorRole ? (
              <div className="text-[10px] text-muted-foreground">{event.actorRole}</div>
            ) : null}
          </div>
        )}
      </td>
      <td className="px-4 py-3 align-top text-xs">
        {event.targetId ? (
          <div>
            <div className="font-medium">{event.targetLabel ?? event.targetId}</div>
            <div className="text-[10px] text-muted-foreground">
              {event.targetKind ? `${event.targetKind} · ` : ''}
              <code className="rounded bg-muted px-1 py-0.5 text-[10px]">
                {event.targetId.length > 12
                  ? `${event.targetId.slice(0, 8)}…${event.targetId.slice(-4)}`
                  : event.targetId}
              </code>
            </div>
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-4 py-3 align-top text-xs text-muted-foreground">
        <MetadataPreview metadata={event.metadata} errorMessage={event.errorMessage} />
      </td>
    </tr>
  )
}

function ActionBadge({ action }: { action: string }) {
  const style = ACTION_STYLES[action]
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1',
        style?.tone ?? 'bg-muted text-muted-foreground ring-border',
      )}
    >
      {style?.label ?? action}
    </span>
  )
}

/**
 * One-line summary of the metadata blob. We pick the most
 * interesting keys (changed fields, scheduled timestamps, role
 * deltas) and render them inline. The full JSON is available via
 * the title attribute on hover. A click-to-expand pane is a
 * follow-up.
 */
function MetadataPreview({
  metadata,
  errorMessage,
}: {
  metadata: Record<string, unknown>
  errorMessage: string | null
}) {
  if (errorMessage) {
    return <span className="text-destructive">{errorMessage}</span>
  }
  const entries = Object.entries(metadata)
  if (entries.length === 0) return <span className="opacity-50">—</span>
  return (
    <span title={JSON.stringify(metadata, null, 2)} className="font-mono text-[11px]">
      {entries
        .slice(0, 3)
        .map(([k, v]) => `${k}=${formatMetaValue(v)}`)
        .join(' ')}
      {entries.length > 3 ? ` +${entries.length - 3}` : ''}
    </span>
  )
}

function formatMetaValue(v: unknown): string {
  if (v === null || v === undefined) return '∅'
  if (typeof v === 'string') {
    if (v.length > 30) return `"${v.slice(0, 27)}…"`
    return `"${v}"`
  }
  if (typeof v === 'object') return JSON.stringify(v).slice(0, 30)
  return String(v)
}

function Pagination({
  offset,
  limit,
  total,
  shown,
  onPrev,
  onNext,
}: {
  offset: number
  limit: number
  total: number
  shown: number
  onPrev: () => void
  onNext: () => void
}) {
  const start = total === 0 ? 0 : offset + 1
  const end = offset + shown
  const hasPrev = offset > 0
  const hasNext = offset + limit < total
  return (
    <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
      <div>
        {start}–{end} of {total}
      </div>
      <div className="flex gap-1">
        <button
          type="button"
          onClick={onPrev}
          disabled={!hasPrev}
          className={cn(
            'inline-flex h-8 items-center gap-1 rounded-md border border-border px-2.5 font-medium',
            'hover:bg-accent hover:text-accent-foreground transition-colors',
            'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent',
          )}
        >
          <ChevronLeft className="h-3 w-3" />
          Prev
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!hasNext}
          className={cn(
            'inline-flex h-8 items-center gap-1 rounded-md border border-border px-2.5 font-medium',
            'hover:bg-accent hover:text-accent-foreground transition-colors',
            'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent',
          )}
        >
          Next
          <ChevronRight className="h-3 w-3" />
        </button>
      </div>
    </div>
  )
}

function formatRelativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  if (Number.isNaN(ms)) return iso
  if (ms < 60_000) return 'just now'
  const mins = Math.floor(ms / 60_000)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mo ago`
  return `${Math.floor(months / 12)}y ago`
}

function formatExactTime(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}
